import type { ISalesperson } from '@bcube/shared';
import { ControlCodes } from '@bcube/shared/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { History, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  type SalespersonAccount,
  type SalespersonFilters,
  salespersonApi,
} from '@/api/commercial/salespersons';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { TableSkeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePermissions } from '@/hooks/use-permissions';
import { toast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-message';
import { maskCpf, maskCpfCnpj } from '@/lib/formatters';

function ErrorMessage({ error, retry }: { error: unknown; retry?: () => void }) {
  return (
    <div role="alert" className="space-y-2 py-3 text-sm text-destructive">
      <p>{getErrorMessage(error)}</p>
      {retry && (
        <Button variant="outline" onClick={retry}>
          Tentar novamente
        </Button>
      )}
    </div>
  );
}

function SalespersonAccountsDialog({
  seller,
  open,
  onOpenChange,
  canEdit,
}: {
  seller: ISalesperson;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<SalespersonAccount[]>([]);
  const [removing, setRemoving] = useState<SalespersonAccount | null>(null);
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timeout);
  }, [search]);
  const current = useQuery({
    queryKey: ['salespersons', seller.id, 'accounts', page],
    queryFn: () => salespersonApi.accounts(seller.id, { page, limit: 10 }),
    enabled: open,
  });
  const available = useQuery({
    queryKey: ['salespersons', seller.id, 'available', debouncedSearch],
    queryFn: () =>
      salespersonApi.accounts(seller.id, {
        page: 1,
        limit: 30,
        search: debouncedSearch,
        available: true,
      }),
    enabled: open && canEdit && seller.active,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['salespersons'] });
  const add = useMutation({
    mutationFn: (accountId: string) => salespersonApi.addAccount(seller.id, accountId),
    onSuccess: () => {
      setSelected([]);
      setSearch('');
      refresh();
      toast({ variant: 'success', title: 'Conta adicionada ao vendedor.' });
    },
  });
  const remove = useMutation({
    mutationFn: (accountId: string) => salespersonApi.removeAccount(seller.id, accountId),
    onSuccess: () => {
      setRemoving(null);
      if (current.data?.accounts.docs.length === 1 && page > 1) setPage(page - 1);
      refresh();
      toast({ variant: 'success', title: 'Conta removida do vendedor.' });
    },
  });
  const busy = add.isPending || remove.isPending;
  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          if (!busy) onOpenChange(value);
        }}
      >
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Clientes de {seller.name}</DialogTitle>
            <DialogDescription>
              Adicione ou remova contas da carteira deste vendedor. Remover o vínculo não exclui a
              conta.
            </DialogDescription>
          </DialogHeader>
          {!seller.active && (
            <p className="text-sm text-muted-foreground">
              Vendedor inativo. A inclusão de contas está desabilitada.
            </p>
          )}
          {canEdit && (
            <div className="space-y-2">
              <label htmlFor="salesperson-account" className="text-sm font-medium">
                Cliente
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="flex-1 min-w-0">
                  <Combobox
                    id="salesperson-account"
                    data={available.data?.accounts.docs ?? []}
                    type="single"
                    keyExtractor={(account) => account.id}
                    displayValueGetter={(account) =>
                      `${account.name} — ${maskCpfCnpj(account.document.value)}`
                    }
                    value={selected}
                    onOptionChange={setSelected}
                    onSearchChange={(value) => setSearch(value)}
                    disableLocalFilter
                    placeholder="Busque por CPF, CNPJ, razão social ou nome fantasia"
                    isLoading={available.isFetching}
                    disabled={busy || !seller.active}
                  />
                </div>
                <Button
                  disabled={!selected.length || busy || !seller.active}
                  onClick={() => add.mutate(selected[0].id)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {add.isPending ? 'Adicionando...' : 'Adicionar'}
                </Button>
              </div>
              {available.error && (
                <ErrorMessage
                  error={available.error}
                  retry={() => {
                    void available.refetch();
                  }}
                />
              )}
              {add.error && <ErrorMessage error={add.error} />}
            </div>
          )}
          {current.isLoading ? (
            <p>Carregando clientes...</p>
          ) : current.error ? (
            <ErrorMessage
              error={current.error}
              retry={() => {
                void current.refetch();
              }}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>CPF/CNPJ</TableHead>
                    <TableHead>Setor</TableHead>
                    {canEdit && <TableHead>Ação</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {current.data?.accounts.docs.length ? (
                    current.data.accounts.docs.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell>{account.name}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {maskCpfCnpj(account.document.value)}
                        </TableCell>
                        <TableCell>{account.sectors.join(', ') || '—'}</TableCell>
                        {canEdit && (
                          <TableCell>
                            <Button
                              variant="outline"
                              size="icon"
                              disabled={busy}
                              aria-label={`Remover ${account.name}`}
                              onClick={() => {
                                remove.reset();
                                setRemoving(account);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={canEdit ? 4 : 3} className="text-center">
                        Nenhuma conta vinculada a este vendedor.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Pagination
                currentPage={page}
                totalPages={current.data?.accounts.pages ?? 0}
                totalItems={current.data?.accounts.total ?? 0}
                limit={10}
                onPageChange={setPage}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={!!removing}
        onOpenChange={(value) => {
          if (!value && !remove.isPending) setRemoving(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover conta do vendedor?</AlertDialogTitle>
            <AlertDialogDescription>
              A conta {removing?.name} continuará cadastrada; somente o vínculo com {seller.name}{' '}
              será removido.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {remove.error && <ErrorMessage error={remove.error} />}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (removing) remove.mutate(removing.id);
              }}
            >
              {remove.isPending ? 'Removendo...' : 'Remover vínculo'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function SalespersonHistoryDialog({
  seller,
  onClose,
}: {
  seller: ISalesperson;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const history = useQuery({
    queryKey: ['salespersons', seller.id, 'history', page],
    queryFn: () => salespersonApi.history(seller.id, page),
  });
  return (
    <Dialog
      open
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Histórico de {seller.name}</DialogTitle>
          <DialogDescription>
            Inclusões e remoções de contas, com data e responsável.
          </DialogDescription>
        </DialogHeader>
        {history.isLoading ? (
          <p>Carregando histórico...</p>
        ) : history.error ? (
          <ErrorMessage
            error={history.error}
            retry={() => {
              void history.refetch();
            }}
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data/hora</TableHead>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Ação / Conta</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.data?.docs.length ? (
                  history.data.docs.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{new Date(entry.createdAt).toLocaleString('pt-BR')}</TableCell>
                      <TableCell>{entry.user.name || entry.user.email}</TableCell>
                      <TableCell>
                        {entry.action}
                        <p className="text-muted-foreground">
                          {entry.metadata
                            .map((field) => String(field.after ?? field.before ?? ''))
                            .join(', ')}
                        </p>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center">
                      Nenhuma alteração registrada.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Pagination
              currentPage={page}
              totalPages={history.data?.pages ?? 0}
              totalItems={history.data?.total ?? 0}
              limit={10}
              onPageChange={setPage}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function SalespersonListPage() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [debouncedCandidateSearch, setDebouncedCandidateSearch] = useState('');
  const [candidate, setCandidate] = useState<ISalesperson[]>([]);
  const [deleting, setDeleting] = useState<ISalesperson | null>(null);
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedCandidateSearch(candidateSearch), 300);
    return () => clearTimeout(timeout);
  }, [candidateSearch]);
  const [name, setName] = useState('');
  const [cpf, setCpf] = useState('');
  const [email, setEmail] = useState('');
  const [filters, setFilters] = useState<SalespersonFilters>({});
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<ISalesperson | null>(null);
  const [viewingHistory, setViewingHistory] = useState<ISalesperson | null>(null);
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(ControlCodes.commercial_salesperson_update);
  const candidates = useQuery({
    queryKey: ['salespersons', 'candidates', debouncedCandidateSearch],
    queryFn: () => salespersonApi.candidates(debouncedCandidateSearch),
    enabled: creating && canEdit,
  });
  const create = useMutation({
    mutationFn: (userId: string) => salespersonApi.create(userId),
    onSuccess: () => {
      setCreating(false);
      setCandidate([]);
      setCandidateSearch('');
      void queryClient.invalidateQueries({ queryKey: ['salespersons'] });
      toast({ variant: 'success', title: 'Vendedor cadastrado.' });
    },
  });
  const unregister = useMutation({
    mutationFn: (id: string) => salespersonApi.delete(id),
    onSuccess: () => {
      setDeleting(null);
      if (result.data?.docs.length === 1 && page > 1) setPage(page - 1);
      void queryClient.invalidateQueries({ queryKey: ['salespersons'] });
      toast({ variant: 'success', title: 'Cadastro de vendedor removido. O usuário foi mantido.' });
    },
  });
  const result = useQuery({
    queryKey: ['salespersons', 'list', filters, page],
    queryFn: () => salespersonApi.list({ ...filters, page, limit: 10 }),
  });
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Vendedores</h1>
          <p className="text-muted-foreground">Gerencie os clientes de cada vendedor.</p>
        </div>
        {canEdit && (
          <Button
            className="bg-black text-white hover:bg-gray-800"
            onClick={() => {
              create.reset();
              setCandidate([]);
              setCandidateSearch('');
              setCreating(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Adicionar vendedor
          </Button>
        )}
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Filtros de busca</CardTitle>
          <CardDescription>Preencha os filtros abaixo e clique em Pesquisar.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
              setFilters({
                name: name.trim() || undefined,
                cpf: cpf.replace(/\D/g, '') || undefined,
                email: email.trim() || undefined,
              });
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Input
                  id="seller-name"
                  label="Nome"
                  placeholder="Filtrar por nome"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
              <div>
                <Input
                  id="seller-cpf"
                  label="CPF"
                  placeholder="Filtrar por CPF"
                  value={cpf}
                  onChange={(event) => setCpf(maskCpf(event.target.value))}
                />
              </div>
              <div>
                <Input
                  id="seller-email"
                  label="Email"
                  placeholder="Filtrar por email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setName('');
                  setCpf('');
                  setEmail('');
                  setFilters({});
                  setPage(1);
                }}
              >
                Limpar
              </Button>
              <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                Pesquisar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      {result.isLoading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <TableSkeleton rows={10} />
        </div>
      ) : result.error ? (
        <ErrorMessage
          error={result.error}
          retry={() => {
            void result.refetch();
          }}
        />
      ) : (
        <div className="w-full space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Clientes</TableHead>
                  <TableHead className="w-[140px]">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.data?.docs.length ? (
                  result.data.docs.map((seller) => (
                    <TableRow key={seller.id}>
                      <TableCell>
                        {seller.name}
                        {!seller.active && (
                          <span className="ml-2 text-xs text-muted-foreground">Inativo</span>
                        )}
                      </TableCell>
                      <TableCell>{maskCpf(seller.cpf)}</TableCell>
                      <TableCell>{seller.email}</TableCell>
                      <TableCell>{seller.clientCount}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Histórico de ${seller.name}`}
                            onClick={() => setViewingHistory(seller)}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Clientes de ${seller.name}`}
                            onClick={() => setEditing(seller)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {canEdit && (
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              aria-label={`Excluir vendedor ${seller.name}`}
                              onClick={() => {
                                unregister.reset();
                                setDeleting(seller);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      Nenhum vendedor encontrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <Pagination
            currentPage={page}
            totalPages={result.data?.pages ?? 0}
            totalItems={result.data?.total ?? 0}
            limit={10}
            onPageChange={setPage}
          />
        </div>
      )}
      {editing && (
        <SalespersonAccountsDialog
          key={editing.id}
          seller={editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          canEdit={canEdit}
        />
      )}
      {viewingHistory && (
        <SalespersonHistoryDialog
          key={viewingHistory.id}
          seller={viewingHistory}
          onClose={() => setViewingHistory(null)}
        />
      )}
      <Dialog
        open={creating}
        onOpenChange={(open) => {
          if (!create.isPending) setCreating(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar vendedor</DialogTitle>
            <DialogDescription>
              Selecione um usuário existente, ativo e desbloqueado.
            </DialogDescription>
          </DialogHeader>
          <Combobox
            data={candidates.data ?? []}
            type="single"
            value={candidate}
            onOptionChange={setCandidate}
            keyExtractor={(user) => user.id}
            displayValueGetter={(user) => `${user.name} — ${maskCpf(user.cpf)} — ${user.email}`}
            placeholder="Buscar usuário por nome"
            onSearchChange={(value) => setCandidateSearch(value)}
            disableLocalFilter
            isLoading={candidates.isFetching}
            disabled={create.isPending}
          />
          {candidates.error && (
            <ErrorMessage
              error={candidates.error}
              retry={() => {
                void candidates.refetch();
              }}
            />
          )}
          {create.error && <ErrorMessage error={create.error} />}
          <Button
            disabled={!candidate.length || create.isPending}
            onClick={() => create.mutate(candidate[0].id)}
          >
            {create.isPending ? 'Salvando...' : 'Adicionar vendedor'}
          </Button>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={!!deleting}
        onOpenChange={(open) => {
          if (!open && !unregister.isPending) setDeleting(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cadastro de vendedor?</AlertDialogTitle>
            <AlertDialogDescription>
              O usuário {deleting?.name} será mantido. Para excluir este cadastro, remova antes os
              vínculos com as contas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {unregister.error && <ErrorMessage error={unregister.error} />}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unregister.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={unregister.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (deleting) unregister.mutate(deleting.id);
              }}
            >
              {unregister.isPending ? 'Excluindo...' : 'Excluir vendedor'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
